import { useEffect } from "react";

/**
 * Production origin used for all canonical / OG tags.
 * Overridable at build time via REACT_APP_CANONICAL_ORIGIN.
 */
const PRODUCTION_ORIGIN =
  process.env.REACT_APP_CANONICAL_ORIGIN || "https://beautix.pl";

/**
 * Detect non-production hosts (preview, staging, localhost, emergent.host)
 * — we don't want them indexed because it would create duplicate content
 * signals for Google. Only beautix.pl (with optional www) is indexable.
 */
const isNonProdHost = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return !(h === "beautix.pl" || h === "www.beautix.pl");
};

/**
 * Lightweight SEO manager — sets document title, meta description,
 * canonical link, OG tags & optional JSON-LD schema for each page.
 * Uses effects only, no external dep like react-helmet.
 */
export default function Seo({
  title,
  description,
  keywords,
  path = "",
  image,
  schema,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    const absImage = image
      ? image.startsWith("http")
        ? image
        : `${PRODUCTION_ORIGIN}${image}`
      : null;
    if (absImage) {
      setMeta("og:image", absImage, "property");
      setMeta("twitter:image", absImage);
    }

    // Canonical — always production origin so preview / localhost duplicates
    // resolve to a single indexable URL for Google.
    const canonicalHref = `${PRODUCTION_ORIGIN}${path || ""}`;
    setMeta("og:url", canonicalHref, "property");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalHref);

    // Prevent preview / localhost hosts from getting indexed as duplicates.
    // On production the default meta robots (index,follow) from index.html stays.
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute(
      "content",
      isNonProdHost()
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large"
    );

    // JSON-LD schema
    if (schema) {
      let script = document.getElementById("page-schema");
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "page-schema";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    }

    // Scroll top on route change
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title, description, keywords, path, image, schema]);

  return null;
}
